"use client";

import Image from "next/image";
import Link from "next/link";
import React, { useState } from "react";

interface BlogCardProps {
  image: string;
  date: string;
  category: string;
  title: string;
  excerpt: string;
  slug?: string;
  basePath?: string;
}

const BlogCard: React.FC<BlogCardProps> = ({
  image,
  date,
  category,
  title,
  excerpt,
  slug = "blog-post",
  basePath = "/blogs",
}) => {
  const [imageError, setImageError] = useState(false);
  const showPlaceholder = !image || image.trim() === '' || imageError;

  return (
    <Link href={`${basePath}/${encodeURIComponent(slug || title.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, ''))}`} className="block group">
      <div className="w-full bg-transparent overflow-hidden transition-all duration-300  hover:shadow-xl">
        {/* Top Image */}
        <div className="relative w-full aspect-video overflow-hidden bg-gradient-to-br from-gray-100 to-gray-200">
          {showPlaceholder ? (
            <div className="w-full h-full bg-gradient-to-br from-blue-100 to-blue-200 flex items-center justify-center">
              <div className="text-center">
                <div className="text-4xl font-bold text-blue-600 mb-2">{title.charAt(0).toUpperCase()}</div>
                <div className="text-xs text-blue-500 font-medium">Blog Post</div>
              </div>
            </div>
          ) : (
            <Image
              src={image}
              alt="Blog thumbnail"
              fill
              className="object-cover sm:object-cover transition-transform duration-500"
              quality={100}
              onError={() => setImageError(true)}
            />
          )}
          {/* Overlay effect */}

          {category && category.trim() !== '' && category.toLowerCase() !== 'blog' && category.toLowerCase() !== 'general' && category.toLowerCase() !== 'blog post' && (
            <span className="absolute top-2 right-0 p-1 px-2 z-[60] bg-white/80 backdrop-blur-2xl rounded-l-md border border-white/12 text-black text-xs   transition-all duration-300  group-hover:scale-105">{category}</span>
          )}

        </div>

        {/* Content Card */}
        <div className="sm:m-4 m-2 ">
          <div className="bg-white/80 backdrop-blur-md px-3 sm:px-6 py-3 sm:py-4 text-[#333] -mt-16 relative z-50 shadow-[0_4px_8px_0_rgba(0,0,0,0.03)] transition-all duration-300 group-hover:bg-white/90 group-hover:shadow-[0_8px_25px_0_rgba(0,0,0,0.1)]">
            {/* Date & Category */}
            <div className="flex justify-between items-center mb-3">
              <span className="font-semibold text-[#1b1b1b] text-sm lg:text-base transition-colors duration-300 group-hover:text-[#272D57]">{date}</span>
            </div>

            {/* Title */}
            <h3 className="text-[#272D57] text-base sm:text-md lg:text-xl line-clamp-2 font-semibold leading-tight mb-2 transition-all duration-300 group-hover:text-[#E0301E]">
              {title}
            </h3>

            {/* Excerpt */}
            <p className="text-[#1b1b1b] leading-relaxed leading-snug text-sm lg:text-base transition-colors duration-300 group-hover:text-[#333] line-clamp-2">{excerpt}</p>
          </div>
        </div>
      </div>
    </Link>
  );
};

export default BlogCard;
